import React, {useMemo, useEffect, useRef, useState} from 'react'
import {Box, HStack, Stack, StackProps, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomApi, useViewport} from '_customHooks'
import {MenuItem, Icon, DarkOverlay} from '_uiComponents'
import {getAnalyticsFirstLevel} from '_analytics/utils/analyticsUtils'
import {useHeaderDispatch, HeaderActionType} from '_context'
import SecondLevelMenu from './partials/second-level'
import TopbarLeft from '../topbar/partials/topbar-left'
import TopbarRight from '../topbar/partials/topbar-right'

interface MenuProps {
    isTransparentMode: boolean
    isAtTop: boolean
    isSearchSectionEnabled: boolean
}

/**
 * @name Menu
 *
 * @component
 *
 * @description
 * Renders a list of header menu items and associated second level menus.
 *
 * @remarks
 * It does not use header state context for proper memoization and performance optimization.
 *
 * @param {MenuProps} props - The properties object.
 * @param {boolean} props.isTransparentMode - Header transparent state.
 * @param {boolean} props.isAtTop - Header isAtTop state.
 * @param {boolean} props.isSearchSectionEnabled - Header isSearchSectionEnabled state.
 *
 * @returns {JSX.Element} - The rendered menu component.
 */
const Menu = ({isTransparentMode, isAtTop, isSearchSectionEnabled}: MenuProps): JSX.Element => {
    const {dispatch} = useHeaderDispatch()
    const {formatMessage} = useIntl()
    const [menuHeight, setMenuHeight] = useState('0px')
    const {locale} = useMultiSite()
    const {useMenuObject} = useCustomApi()
    const isTouchEnabled = useRef(false)
    const {isDesktop} = useViewport()

    // const {data: firstLevelCategories} = useMenuObject('1', locale.id)
    const {data: secondLevelCategories} = useMenuObject('2', locale.id)

    const [currentLevel, setCurrentLevel] = useState(isDesktop ? 2 : 1)
    const [showSecondLevelMenu, setShowSecondLevelMenu] = useState<number | null>(null)
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const navbarAriaLabel = formatMessage({id: 'accessibility.label.navBar', defaultMessage: 'Main menu, select a category.'})

    const showTopbarContent = useMemo(() => !isDesktop && showSecondLevelMenu === null, [isDesktop, showSecondLevelMenu])

    const overrideTransparentMode = isTransparentMode && showSecondLevelMenu === null
    const colorMode = isTransparentMode && showSecondLevelMenu === null ? 'light' : 'dark'

    const styles = useMultiStyleConfig('HeaderMenu', {
        isTransparentMode: overrideTransparentMode,
        isAtTop,
        menuHeight,
        showTopbarContent,
    }) as Record<string, StackProps>

    const openMenuItem = (index: number) => {
        if (!isSearchSectionEnabled) {
            setShowSecondLevelMenu(index)
            setCurrentLevel(2)

            if (isDesktop && !isTouchEnabled.current) {
                dispatch({type: HeaderActionType.HOVER_MENU_ITEM, payload: true})
            }
        }
    }

    const goBackToFirstLevel = () => {
        setShowSecondLevelMenu(null)
        setCurrentLevel(1)
    }

    const closeMenu = () => {
        if (isDesktop) {
            setShowSecondLevelMenu(null)
            setMenuHeight('0px')
            dispatch({type: HeaderActionType.HOVER_MENU_ITEM, payload: false})
        } else {
            dispatch({type: HeaderActionType.TOGGLE_SIDEBAR})
        }
    }

    const handleMouseEnter = (index: number) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
        }
        const timeout = setTimeout(() => {
            if (isDesktop && !isTouchEnabled.current) {
                openMenuItem(index)
            }
        }, 350)
        hoverTimeoutRef.current = timeout
    }

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
            hoverTimeoutRef.current = null
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (showSecondLevelMenu === index) {
                closeMenu()
            } else {
                openMenuItem(index)
            }
        }
        if (event.key === 'Escape') {
            closeMenu()
        }
    }

    useEffect(() => {
        if (window && window.matchMedia('(pointer: coarse)').matches) {
            isTouchEnabled.current = true
        }

        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current)
            }
        }
    }, [])

    return (
        <VStack {...styles.container}>
            {showSecondLevelMenu !== null && isDesktop && <DarkOverlay variant="headerMenu" />}

            <HStack as="nav" data-testid="navbar" aria-label={navbarAriaLabel} {...styles.navbar} onMouseLeave={isDesktop ? closeMenu : undefined}>
                {secondLevelCategories?.categories?.map((category, index) => {
                    return (
                        <Box key={`${category.id}-${index}`} {...styles.firstLevelBox}>
                            <Box
                                {...styles.firstLevelItemBox}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                                data-testid={`first-level-menu-item-${index}`}
                            >
                                {isDesktop ? (
                                    <>
                                        {category.url && !isTouchEnabled.current ? (
                                            <MenuItem
                                                colorMode={colorMode}
                                                href={category.url}
                                                variant="header"
                                                closeMenu={closeMenu}
                                                analyticsEventData={getAnalyticsFirstLevel(category, index)}
                                                renderaslink={true}
                                                arrow="none"
                                                ariaLabel={formatMessage(
                                                    {id: 'accessibility.label.menu.item', defaultMessage: 'Go to {0}'},
                                                    {0: category.name},
                                                )}
                                            >
                                                {category.name.toUpperCase()}
                                            </MenuItem>
                                        ) : (
                                            <Box
                                                {...styles.firstLevelLabel}
                                                aria-hidden="true"
                                                onClick={() => openMenuItem(index)}
                                                cursor="pointer"
                                            >
                                                {category.name.toUpperCase()}
                                            </Box>
                                        )}
                                        <Box
                                            as="button"
                                            type="button"
                                            {...styles.focusArrow}
                                            {...(showSecondLevelMenu === index ? styles.focusArrowActive : undefined)}
                                            onClick={() => {
                                                if (showSecondLevelMenu === index) {
                                                    closeMenu()
                                                } else {
                                                    openMenuItem(index)
                                                }
                                            }}
                                            onKeyDown={(event) => handleKeyDown(event, index)}
                                            aria-expanded={showSecondLevelMenu === index}
                                            aria-label={formatMessage({id: 'label.menu.overlay.category', defaultMessage: 'Open menu {0}'}, {0: category.name})}
                                            aria-controls={'menu-' + index}
                                            data-testid={`first-level-menu-keyboard-${index}`}
                                        >
                                            <Icon name="IconChevronDown" size="xs" />
                                        </Box>
                                    </>
                                ) : (
                                    <Box onClick={() => openMenuItem(index)} width="100%">
                                        <MenuItem
                                            colorMode={colorMode}
                                            href={category.url || '/'}
                                            variant="header"
                                            analyticsEventData={getAnalyticsFirstLevel(category, index)}
                                            renderaslink={false}
                                            arrow="right"
                                            ariaLabel={formatMessage({id: 'accessibility.label.menu.item', defaultMessage: 'Go to {0}'}, {0: category.name})}
                                        >
                                            {category.name.toUpperCase()}
                                        </MenuItem>
                                    </Box>
                                )}
                            </Box>

                            {secondLevelCategories && currentLevel === 2 && showSecondLevelMenu === index && (
                                <SecondLevelMenu
                                    category={secondLevelCategories.categories[index]}
                                    type={category.type}
                                    menuStaticLibrary={secondLevelCategories.menuStaticLibrary}
                                    showSecondLevelMenu={showSecondLevelMenu}
                                    index={index}
                                    setMenuHeight={setMenuHeight}
                                    goBackToFirstLevel={goBackToFirstLevel}
                                    closeMenu={closeMenu}
                                    tabIndex={showSecondLevelMenu === index ? 0 : -1}
                                    isAtTop={isAtTop}
                                />
                            )}
                        </Box>
                    )
                })}
            </HStack>

            {showTopbarContent && (
                <Stack {...styles.topbarContent}>
                    <TopbarRight isInSidebar />
                    <TopbarLeft isInSidebar />
                </Stack>
            )}
        </VStack>
    )
}

/**
 * Custom equal function to memoize the menu component.
 *
 * @param {MenuProps} prevProps
 * @param {MenuProps} nextProps
 * @returns {boolean} - True if transparent mode hasn't changed, false otherwise.
 */
const areEqual = (prevProps: MenuProps, nextProps: MenuProps): boolean => {
    return (
        prevProps.isAtTop === nextProps.isAtTop &&
        prevProps.isTransparentMode === nextProps.isTransparentMode &&
        nextProps.isSearchSectionEnabled === prevProps.isSearchSectionEnabled
    )
}

export default React.memo(Menu, areEqual)
